import {
  API_AI_PROJECT_SCHEDULES,
  API_AI_SCHEDULE,
  API_AI_SCHEDULE_PREVIEW_LOANS,
  API_AI_SCHEDULE_RUN,
  API_AI_SCHEDULE_RUNS,
  API_AI_SCHEDULES,
} from 'config/api.url.constants'
import Api from 'services/api'

/**
 * How often a scheduled run repeats. Deliberately a short list rather than a cron expression -
 * every cadence anyone has asked for is one of these five.
 */
export type AiScheduleFrequency = 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly'

/**
 * Where a schedule files the chat its runs produce.
 * - 'continuous': one thread for good, and the model is shown its last few turns, so a digest can
 *   answer in terms of what it said last time.
 * - 'perRun': a new thread each run, for an answer that should stand on its own.
 */
export type AiScheduleThreadMode = 'continuous' | 'perRun'

/** 'queued' is a run that has been planned but not started - a fan-out writes all of them up front. */
export type AiScheduleRunStatus = 'queued' | 'running' | 'success' | 'error' | 'skipped'

/**
 * Pipeline filters selecting the loans a schedule runs over, in the shape the pipeline already
 * sends. Stored as the filter, not as a resolved list, so "every loan tagged Statement" keeps
 * meaning that as loans are tagged and untagged.
 */
export interface AiScheduleLoanFilters {
  tags?: string[]
  tagMatch?: 'any' | 'all'
  excludeTags?: string[]
  pipelineType?: string
  status?: string[]
  [key: string]: any
}

export interface AiSchedule {
  id: number
  projectId: number
  name: string
  prompt: string
  /** The loan every run is about, when the schedule is loan-scoped. */
  loanNumber?: number
  /** Set instead of `loanNumber` when the schedule runs over a set of loans. */
  loanFilters?: AiScheduleLoanFilters | null
  /** The most loans one firing will run; each loan is a full agentic turn. */
  maxLoansPerRun: number
  /** How many loans the filter matched last time, so "ran 25 of 212" is visible. */
  lastMatchedCount: number
  frequency: AiScheduleFrequency
  /** 'HH:mm' wall clock, read in `timezone`. */
  timeOfDay: string
  /** 0 = Sunday .. 6 = Saturday, for a weekly schedule. Empty means every day. */
  daysOfWeek: number[]
  dayOfMonth?: number
  minuteOfHour: number
  timezone: string
  /** The single instant a 'once' schedule fires. */
  runAt?: string
  isEnabled: boolean
  threadMode: AiScheduleThreadMode
  /** The rolling thread of a 'continuous' schedule, once its first run has made one. */
  conversationId?: number
  historyTurns: number
  notifyEmails: string[]
  createdBy: string
  nextRunAt?: string
  lastRunAt?: string
  lastStatus?: AiScheduleRunStatus | ''
  lastError?: string
  runCount: number
  createdAt: string
  updatedAt: string
  /** The cadence in plain English, rendered by the server - show this rather than rebuilding it. */
  description?: string
  /** Only on the across-projects list. */
  projectName?: string
}

/** One attempt, including the ones that failed - a run that threw never reaches a transcript. */
export interface AiScheduleRun {
  id: number
  scheduleId: number
  projectId?: number
  loanNumber?: number
  conversationId?: number
  trigger: 'schedule' | 'manual'
  /** One id across every loan of a fan-out, so the runs of one firing belong together. */
  batchId: string
  status: AiScheduleRunStatus
  prompt: string
  answer: string
  steps: any[]
  error: string
  startedAt?: string
  finishedAt?: string
  durationMs?: number
  createdAt: string
}

export type AiScheduleInput = Partial<
  Pick<
    AiSchedule,
    | 'name'
    | 'prompt'
    | 'loanNumber'
    | 'loanFilters'
    | 'maxLoansPerRun'
    | 'frequency'
    | 'timeOfDay'
    | 'daysOfWeek'
    | 'dayOfMonth'
    | 'minuteOfHour'
    | 'timezone'
    | 'runAt'
    | 'isEnabled'
    | 'threadMode'
    | 'historyTurns'
    | 'notifyEmails'
  >
>

/** Pass a loan number to get that loan's schedules plus the project's unscoped ones. */
export const getAiProjectSchedules = (id: number, loanNumber?: number): Promise<AiSchedule[]> =>
  Api.get(API_AI_PROJECT_SCHEDULES, loanNumber ? { loanNumber } : {}, { id })

/** Every schedule the user can see, across every project they have access to. */
export const getAiSchedules = (): Promise<AiSchedule[]> => Api.get(API_AI_SCHEDULES)

export const createAiSchedule = (id: number, data: AiScheduleInput): Promise<AiSchedule> =>
  Api.post(API_AI_PROJECT_SCHEDULES, data, { id })

export const updateAiSchedule = (scheduleId: number, data: AiScheduleInput): Promise<AiSchedule> =>
  Api.put(API_AI_SCHEDULE, data, { scheduleId })

export const deleteAiSchedule = (scheduleId: number): Promise<{ success: boolean }> =>
  Api.delete(API_AI_SCHEDULE, {}, { scheduleId })

/** Pass a loan number to narrow a fan-out schedule's history to one loan. */
export const getAiScheduleRuns = (scheduleId: number, limit = 50, loanNumber?: number): Promise<AiScheduleRun[]> =>
  Api.get(API_AI_SCHEDULE_RUNS, loanNumber ? { limit, loanNumber } : { limit }, { scheduleId })

/**
 * How many loans a filter selects right now, so the dialog can say "212 loans match" before the
 * schedule is saved rather than after its first firing.
 */
export const previewAiScheduleLoans = (
  id: number,
  loanFilters: AiScheduleLoanFilters,
): Promise<{ count: number; loanNumbers: number[] }> =>
  Api.post(API_AI_SCHEDULE_PREVIEW_LOANS, { loanFilters }, { id })

/**
 * Start a run now. It comes back immediately with status 'running' and finishes in the background -
 * an agentic turn can take minutes - so the caller polls `getAiScheduleRuns` for the answer.
 *
 * `loanNumber` pins the run to that one loan whatever the schedule's own scope says, which is what
 * running a project from inside a loan file means.
 */
export const runAiScheduleNow = (scheduleId: number, loanNumber?: number): Promise<AiScheduleRun> =>
  Api.post(API_AI_SCHEDULE_RUN, loanNumber ? { loanNumber } : {}, { scheduleId })
